import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Plus, Check, Building2 } from "lucide-react";

/** Autocomplete + create-on-the-fly Franchiseur picker. props: value (nom), onSave(nom) */
export default function FranchiseurSelector({ value = "", onSave, placeholder = "Choisir ou créer un franchiseur…", testid = "franchiseur-selector", className = "" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const load = async (search = "") => {
    const { data } = await api.get("/franchiseurs/list", { params: search ? { q: search } : {} });
    setItems(Array.isArray(data) ? data : (data.items || []));
  };
  useEffect(() => { if (open) load(q); }, [q, open]);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const exactMatch = items.find((p) => (p.nom || "").toLowerCase() === q.trim().toLowerCase());

  const pick = async (nom) => {
    if (!nom?.trim()) return;
    await api.post("/franchiseurs/upsert-by-name", { nom });
    onSave?.(nom);
    setQ(""); setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, items.length)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight === items.length || (!items.length && q.trim())) pick(q.trim());
      else if (items[highlight]) pick(items[highlight].nom);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className={`relative group ${className}`} ref={wrapRef}>
      {!open ? (
        <div data-testid={testid}
             onClick={() => { setOpen(true); setQ(""); setTimeout(() => inputRef.current?.focus(), 0); }}
             className="cursor-text hover:bg-stone-50 border border-stone-200 rounded px-3 py-2 min-h-[40px] flex items-center gap-2">
          {value ? <span className="inline-flex items-center gap-1.5 text-sm"><Building2 size={12} className="text-stone-400"/>{value}</span>
                 : <span className="text-stone-400 italic text-sm">{placeholder}</span>}
        </div>
      ) : (
        <>
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setHighlight(0); }}
                 onKeyDown={onKey} placeholder={value || placeholder} data-testid={`${testid}-input`}
                 className="w-full border border-[#C26B53]/40 focus:border-[#C26B53] outline-none rounded px-3 py-2 text-sm bg-white"/>
          <div className="absolute z-30 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
            {items.map((p, idx) => (
              <button key={p.id || p.nom} type="button"
                      onMouseEnter={() => setHighlight(idx)}
                      onMouseDown={(e) => { e.preventDefault(); pick(p.nom); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${highlight === idx ? "bg-[var(--pink-powder)]/40" : ""}`}>
                <span className="flex-1">{p.nom}</span>
                {p.nom === value && <Check size={12} className="text-emerald-700"/>}
              </button>
            ))}
            {q.trim() && !exactMatch && (
              <button type="button" onMouseEnter={() => setHighlight(items.length)}
                      onMouseDown={(e) => { e.preventDefault(); pick(q.trim()); }}
                      data-testid={`${testid}-create`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-t border-stone-100 ${highlight === items.length ? "bg-[var(--pink-powder)]/40" : ""}`}>
                <Plus size={12} className="text-[var(--ppd)]"/>
                <span>Créer « <span className="font-semibold">{q.trim()}</span> »</span>
              </button>
            )}
            {items.length === 0 && !q.trim() && (
              <div className="px-3 py-4 text-xs text-stone-400 italic">Tapez pour rechercher…</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
